import * as atStepTree from '@codegrade/autotest-step-tree';
import * as atUtils from '@codegrade/autotest-utils';
import { Maybe } from '@codegrade/maybe';
import { expr, map, parsers } from '@codegrade/utils';
import * as atQuizUtils from './utils';
export * from './types';
export * as utils from './utils';
/** The file extension used for the answer files. */
export const ANSWERS_FILE_EXTENSION = '.cgq';
/**
 * Determine whether a file is a quiz file by checking the file extension.
 *
 * @param fileName The file name to check.
 * @returns Whether the file name is for a quiz file.
 */
export function isQuizFile(fileName) {
    return fileName.endsWith(ANSWERS_FILE_EXTENSION);
}
/**
 * Get the name as configured in the quiz step from the answers file name.
 *
 * @param answersFileName The path of the answers file.
 * @returns The name as configured in the quiz step.
 */
export function cleanupQuizName(answersFileName) {
    let quizName = answersFileName.replace(/^\/*/, '');
    if (isQuizFile(quizName)) {
        quizName = quizName.slice(0, -ANSWERS_FILE_EXTENSION.length);
    }
    return quizName;
}
/**
 * Check if the given step is a quiz step.
 *
 * @param step The step to check for.
 * @returns If the given step is a quiz step.
 */
function isQuizStep(step) {
    return step.type === 'quiz';
}
/**
 * Get all questions for the requested quiz.
 *
 * @param answersFileName The file name of the quiz answers file.
 * @param config The AutoTest configuration containing the questions.
 * @returns Either the quiz questions, or an error if the AT configuration could not be parsed.
 */
export function getConfigurationFromATConfig(answersFileName, config) {
    const quizName = cleanupQuizName(answersFileName);
    const questions = [];
    /**
     * Called once we found the correct quiz, it just adds all quiz question steps to the correct
     * questions array.
     *
     * @param quiz The quiz we found for which we should add all steps.
     */
    function collectQuiz(quiz) {
        for (const question of quiz.flattened()) {
            if (question.isStepOfType(atUtils.isQuizQuestion)) {
                questions.push({
                    quiz: quiz.step,
                    question,
                });
            }
        }
    }
    /**
     * Traverse the entire step tree to get all questions for the requested quiz.
     *
     * @param item The current item under inspection.
     */
    function traverse(item) {
        // Check if this is the correct quiz, if it is collect all steps under it, otherwise we
        // recurse.
        if (item.isStepOfType(isQuizStep)) {
            const fileName = cleanupQuizName(item.step.fileName);
            if (fileName === quizName) {
                collectQuiz(item);
                return;
            }
        }
        for (const child of item.children) {
            traverse(child);
        }
    }
    traverse(config);
    return { questions };
}
/**
 * Get the initial, empty answer for the given question step.
 *
 * @param step The question to get the initial answer for.
 * @returns The initial answer for the given step.
 */
export function initialAnswerForStep(step) {
    return expr.match(step, 'type', {
        mcq: mcq => ({
            tag: mcq.type,
            id: Maybe.empty(),
        }),
        'select-all-question': selectAll => ({
            tag: selectAll.type,
            ids: Maybe.empty(),
        }),
        'prompt-engineering-question': promptEngineeringQuestion => ({
            tag: promptEngineeringQuestion.type,
            answerLanguage: promptEngineeringQuestion.answerLanguage,
            answer: '',
            edited: false,
        }),
        'coding-question': codingQuestion => ({
            tag: codingQuestion.type,
            language: Maybe.of(atQuizUtils.getCodingQuestionLanguage(codingQuestion)),
            answer: codingQuestion.starterCode,
            edited: Maybe.of(false),
        }),
    });
}
/**
 * Create test answer files from an ATv2 configuration.
 *
 * @param config The ATv2 test configuration.
 * @returns A mapping from file name to file contents if no error occurred,
 * otherwise the error that occurred.
 */
export function createAnswerFilesFromATConfig(config) {
    const files = new map.MaybeMap();
    /**
     * Traverse the entire step tree to run a callback for each question in the quiz.
     *
     * When a Quiz step is encountered, it is pushed onto the stack of "active" quizzes.
     *
     * When a Multiple Choice Question step is encountered, its data is pushed to the
     * answers file for each active quiz.
     *
     * @param item The current item under inspection.
     * @param quizzes Stack of currently active quizzes.
     */
    function traverse(item, quizzes) {
        let newQuizzes = quizzes;
        if (item.isStepOfType(isQuizStep)) {
            const fileName = cleanupQuizName(item.step.fileName);
            newQuizzes = newQuizzes.concat(`/${fileName}${ANSWERS_FILE_EXTENSION}`);
        }
        else if (item.isStepOfType(atUtils.isQuizQuestion)) {
            for (const quiz of quizzes) {
                const file = files.getOrInsertWith(quiz, () => ({ answers: {} }));
                file.answers[item.step.id] = initialAnswerForStep(item.step);
            }
        }
        for (const child of item.children) {
            traverse(child, newQuizzes);
        }
    }
    return atStepTree.create(config.steps).map(tree => {
        traverse(tree, []);
        return files;
    });
}
/**
 * Check whether a question has been interacted with.
 *
 * @param questionId The id of the question.
 * @param answer The given answer.
 * @param questionFiles Mapping from question id to file name configured on coding and prompt questions.
 * @param regularFiles The paths of other files that the student submitted.
 * @returns Whether the question has been interacted with.
 */
function isQuestionInteractedWith(questionId, answer, questionFiles, regularFiles) {
    return expr.match(answer, 'tag', {
        mcq: ({ id }) => id.isJust(),
        'select-all-question': ({ ids }) => ids.isJust(),
        'prompt-engineering-question': ({ edited }) => {
            // This is currently copying what is done below with
            // coding-question, but instead looking at the answerFile, which is
            // the prompt written for this problem.
            if (edited) {
                return true;
            }
            const fileName = questionFiles.get(questionId);
            return fileName != null && regularFiles.has(fileName);
        },
        'coding-question': ({ edited }) => {
            // We default to true because we don't know for existing answers whether
            // they had been interacted with or not, so we assume it has been.
            if (edited.orDefault(true)) {
                return true;
            }
            const fileName = questionFiles.get(questionId);
            return fileName != null && regularFiles.has(fileName);
        },
    });
}
/**
 * Get the names of all quizzes that have questions that have not been interacted with.
 *
 * Note: when a coding question has not been interacted with, but a file with the same name
 * as configured on the coding question exists in `regularFiles`, then that question will be
 * considered interacted with.
 *
 * @param answerFiles All quiz answer files of the submission.
 * @param testConfig The ATv2 test configuration.
 * @param regularFiles The paths of other files that are part of the submission.
 * @returns A list of paths of all quiz answer files that have at least one question that
 * the student hasn't interacted with.
 */
export function getNonInteractedQuizzes(answerFiles, testConfig, regularFiles) {
    const allQuestionIds = new Set();
    const questionFiles = new Map();
    for (const step of testConfig.steps) {
        if (atUtils.isQuizQuestion(step)) {
            allQuestionIds.add(step.id);
            let fileName = '';
            if (step.type === 'coding-question') {
                fileName = step.studentFileName.replace(/\/+/, '/').replace(/^\//, '');
            }
            else if (step.type === 'prompt-engineering-question') {
                fileName = step.answerFilename.replace(/\/+/, '/').replace(/^\//, '');
            }
            questionFiles.set(step.id, fileName);
        }
    }
    const notInteractedWithAnswerFiles = [];
    for (const answersFile of answerFiles) {
        const allInteracted = Object.entries(answersFile.data.answers).every(([questionId, answer]) => {
            // Question doesn't exist anymore, so we should ignore it.
            if (!allQuestionIds.has(questionId)) {
                return true;
            }
            return isQuestionInteractedWith(questionId, answer, questionFiles, regularFiles);
        });
        if (!allInteracted) {
            notInteractedWithAnswerFiles.push(cleanupQuizName(answersFile.path));
        }
    }
    return notInteractedWithAnswerFiles;
}
/**
 * Make a parser for a given answer on a multiple choice question.
 *
 * @param baseParsers The parsers to use.
 * @returns A parser for a given multiple choice question answer.
 */
export function makeMultipleChoiceAnswerParser(baseParsers) {
    return baseParsers.interface({
        tag: baseParsers.exactString('mcq'),
        id: baseParsers.maybe(baseParsers.string),
    });
}
/**
 * Make a parser for a given answer on a multiple choice question.
 *
 * @param baseParsers The parsers to use.
 * @returns A parser for a given multiple choice question answer.
 */
export function makeSelectAllAnswerParser(baseParsers) {
    return baseParsers.interface({
        tag: baseParsers.exactString('select-all-question'),
        ids: baseParsers.maybe(baseParsers.strRecord(baseParsers.boolean)),
    });
}
/**
 * Make a parser for a given answer on a coding question.
 *
 * @param baseParsers The parsers to use.
 * @returns A parser for a given coding question answer.
 */
export function makeCodingQuestionAnswerParser(baseParsers) {
    return baseParsers.interface({
        tag: baseParsers.exactString('coding-question'),
        language: baseParsers.maybe(baseParsers.string),
        answer: baseParsers.text,
        edited: baseParsers.maybe(baseParsers.boolean),
    });
}
/**
 * Make a parser for a given answer on a prompt engineering question.
 *
 * @param baseParsers The parsers to use.
 * @returns A parser for a given prompt engineering question answer.
 */
export function makePromptEngineeringQuestionAnswerParser(baseParsers) {
    return baseParsers.interface({
        tag: baseParsers.exactString('prompt-engineering-question'),
        answerLanguage: baseParsers.string,
        answer: baseParsers.text,
        edited: baseParsers.boolean,
    });
}
/**
 * Make a parser for the given answers of a quiz.
 *
 * @param baseParsers The parsers to use.
 * @returns A parser for the given answers.
 */
export function makeAnswerParser(baseParsers) {
    return parsers.taggedUnion('tag', [
        ['mcq', makeMultipleChoiceAnswerParser(baseParsers)],
        ['select-all-question', makeSelectAllAnswerParser(baseParsers)],
        ['prompt-engineering-question', makePromptEngineeringQuestionAnswerParser(baseParsers)],
        ['coding-question', makeCodingQuestionAnswerParser(baseParsers)],
    ], undefined, baseParsers);
}
/**
 * The parser to create the content under the top level answers key.
 *
 * We extract this so that we can use it in other places to set a map under key
 * 'answers' in the answers file.
 *
 * @param baseParsers The parsers to use.
 * @returns A parser for the given answers.
 */
export function makeAnswersParser(baseParsers) {
    return baseParsers.strRecord(makeAnswerParser(baseParsers));
}
/**
 * Make a parser for an entire quiz answers file.
 *
 * @param baseParsers The parsers to use.
 * @returns A parser for quiz answer files.
 */
export function makeAnswersFileParser(baseParsers) {
    return baseParsers.interface({
        answers: makeAnswersParser(baseParsers),
    });
}
/** A parser for an entire quiz answers file. */
export const baseAnswersFileParser = makeAnswersFileParser(parsers.DEFAULT_BASE_PARSERS);
